import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, Loader2 } from 'lucide-react';
import Seo from '../components/Seo';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Algo salió mal. Inténtalo de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#C8E7F2] via-white to-[#FFC9C8] px-4">
      <Seo fullTitle="Acceso" path="/admin/login" noindex />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Logo" className="h-20 w-20 rounded-full object-cover mx-auto mb-4 shadow-lg" />
          <h1 className="text-2xl font-bold text-[#4A4A4A]">Panel de Administración</h1>
          <p className="text-gray-500 text-sm mt-1">SPANISCH EN LA RED</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                required
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none transition-all"
                placeholder="tu@email.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                required
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-60 flex items-center justify-center space-x-2"
          >
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Entrando...</span></> : <span>Iniciar Sesión</span>}
          </button>
        </form>

        <button
          onClick={() => navigate('/')}
          className="w-full mt-4 text-sm text-gray-500 hover:text-[#7DBDD9] transition-colors"
        >
          ← Volver a la web
        </button>
      </motion.div>
    </div>
  );
}
